import React, { useRef } from 'react';
import { Link } from 'react-router-dom';
import { motion, useInView } from 'framer-motion';
import { ArrowRight, CheckCircle } from 'lucide-react';

const fadeUp = {
  hidden: { opacity: 0, y: 40 },
  visible: (i = 0) => ({
    opacity: 1, y: 0,
    transition: { duration: 0.6, delay: i * 0.1, ease: [0.25, 0.46, 0.45, 0.94] }
  })
};

const services = [
  {
    icon: '🏍️', label: 'Mécanique Moto', color: '#FF6B00',
    desc: 'Révision complète, moteur, freins, carburation, transmission.',
    details: ['Révision moteur', 'Réparation freins', 'Carburation', 'Transmission', 'Démarrage électrique'],
  },
  {
    icon: '🚗', label: 'Mécanique Voiture', color: '#1A3B5D',
    desc: 'Vidange, embrayage, distribution, boîte de vitesse.',
    details: ['Vidange & filtres', 'Embrayage', 'Distribution', 'Boîte de vitesse', 'Suspension'],
  },
  {
    icon: '🔩', label: 'Vulcanisateur', color: '#10B981',
    desc: 'Pneus, équilibrage, gonflage, montage rapide.',
    details: ['Réparation pneus', 'Équilibrage', 'Montage / démontage', 'Gonflage azote', 'Crevaison urgence'],
  },
  {
    icon: '⚡', label: 'Électricité Auto', color: '#F59E0B',
    desc: 'Batterie, alternateur, câblage, diagnostic électronique.',
    details: ['Batterie & alternateur', 'Câblage électrique', 'Diagnostic OBD', 'Démarreur', 'Éclairage'],
  },
  {
    icon: '🎨', label: 'Carrosserie', color: '#8B5CF6',
    desc: 'Débosselage, soudure, peinture, remise en forme.',
    details: ['Débosselage', 'Soudure carrosserie', 'Peinture & retouche', 'Remplacement vitres', 'Anti-rouille'],
  },
  {
    icon: '❄️', label: 'Climatisation', color: '#06B6D4',
    desc: 'Recharge gaz, compresseur, entretien système froid.',
    details: ['Recharge gaz R134a', 'Compresseur', 'Condenseur', 'Filtre habitacle', 'Diagnostic fuite'],
  },
  {
    icon: '🔧', label: 'Vidange & Entretien', color: '#EF4444',
    desc: 'Entretien régulier, vidange huile, révision complète.',
    details: ['Vidange huile moteur', 'Filtre à air / huile', 'Bougies', 'Courroie accessoires', 'Check complet'],
  },
  {
    icon: '🛒', label: 'Pièces Détachées', color: '#84CC16',
    desc: 'Fourniture et pose de pièces d\'origine ou équivalentes.',
    details: ['Pièces d\'origine', 'Pièces équivalentes', 'Import commande', 'Pose incluse', 'Garantie pièces'],
  },
];

const ServiceCard = ({ service, index }) => {
  const ref = useRef(null);
  const inView = useInView(ref, { once: true, margin: '-60px' });
  return (
    <motion.div
      ref={ref}
      custom={index % 3}
      variants={fadeUp}
      initial="hidden"
      animate={inView ? 'visible' : 'hidden'}
      style={{
        background: 'white',
        borderRadius: '16px',
        padding: '2rem',
        boxShadow: '0 4px 24px rgba(0,0,0,0.08)',
        border: '1px solid rgba(0,0,0,0.06)',
        transition: 'transform 0.3s, box-shadow 0.3s',
        cursor: 'default',
      }}
      whileHover={{ y: -6, boxShadow: '0 12px 40px rgba(0,0,0,0.14)' }}
    >
      <div style={{
        width: 64, height: 64, borderRadius: '16px',
        background: service.color + '18',
        display: 'flex', alignItems: 'center', justifyContent: 'center',
        fontSize: '2rem', marginBottom: '1.2rem',
      }}>
        {service.icon}
      </div>
      <h3 style={{ fontFamily: 'var(--font-display)', fontSize: '1.2rem', color: 'var(--blue-dark)', marginBottom: '0.5rem', letterSpacing: '1px' }}>
        {service.label}
      </h3>
      <p style={{ color: 'var(--gray-dark)', fontSize: '0.9rem', marginBottom: '1.2rem', lineHeight: 1.6 }}>
        {service.desc}
      </p>
      <ul style={{ listStyle: 'none', padding: 0, margin: 0, display: 'flex', flexDirection: 'column', gap: '0.4rem' }}>
        {service.details.map(d => (
          <li key={d} style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', fontSize: '0.85rem', color: 'var(--gray-dark)' }}>
            <CheckCircle size={14} color={service.color} />
            {d}
          </li>
        ))}
      </ul>
      <Link
        to={`/prestataires?service=${service.label}`}
        style={{
          display: 'inline-flex', alignItems: 'center', gap: '0.4rem',
          marginTop: '1.5rem', color: service.color,
          fontWeight: 700, fontSize: '0.85rem', textDecoration: 'none',
          fontFamily: 'var(--font-cond)', letterSpacing: '1px', textTransform: 'uppercase',
        }}
      >
        Trouver un prestataire <ArrowRight size={14} />
      </Link>
    </motion.div>
  );
};

export default function ServicesPage() {
  return (
    <div style={{ paddingTop: '80px', background: 'var(--gray-light)', minHeight: '100vh' }}>

      {/* HERO */}
      <div style={{
        background: 'linear-gradient(135deg, var(--blue-dark) 0%, #0D2137 100%)',
        padding: '5rem 0 4rem',
        textAlign: 'center',
      }}>
        <div className="container">
          <motion.div initial={{ opacity: 0, y: 30 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.6 }}>
            <div style={{
              display: 'inline-block',
              background: 'rgba(255,107,0,0.15)',
              border: '1px solid rgba(255,107,0,0.3)',
              borderRadius: '50px', padding: '0.4rem 1.2rem',
              color: 'var(--orange)', fontSize: '0.8rem',
              fontFamily: 'var(--font-cond)', letterSpacing: '2px',
              textTransform: 'uppercase', marginBottom: '1.5rem',
            }}>
              Nos Services
            </div>
            <h1 style={{
              fontFamily: 'var(--font-display)',
              fontSize: 'clamp(2rem, 5vw, 3.5rem)',
              color: 'white', letterSpacing: '2px',
              marginBottom: '1rem',
            }}>
              TOUS VOS BESOINS<br />
              <span style={{ color: 'var(--orange)' }}>AUTOMOBILE</span>
            </h1>
            <p style={{ color: 'rgba(255,255,255,0.65)', fontSize: '1.1rem', maxWidth: 560, margin: '0 auto 2rem' }}>
              Des mécaniciens qualifiés pour chaque type de prestation, partout au Bénin.
            </p>
            <Link to="/prestataires" className="btn btn-primary btn-lg">
              Trouver un prestataire <ArrowRight size={18} style={{ marginLeft: 8 }} />
            </Link>
          </motion.div>
        </div>
      </div>

      {/* GRID */}
      <div className="container" style={{ padding: '4rem 1rem' }}>
        <div style={{
          display: 'grid',
          gridTemplateColumns: 'repeat(auto-fill, minmax(300px, 1fr))',
          gap: '1.5rem',
        }}>
          {services.map((s, i) => <ServiceCard key={s.label} service={s} index={i} />)}
        </div>
      </div>

      {/* CTA */}
      <div style={{ background: 'var(--blue-dark)', padding: '4rem 0', textAlign: 'center' }}>
        <div className="container">
          <motion.div initial={{ opacity: 0, y: 20 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }}>
            <h2 style={{ fontFamily: 'var(--font-display)', color: 'white', fontSize: 'clamp(1.5rem, 4vw, 2.5rem)', letterSpacing: '2px', marginBottom: '1rem' }}>
              BESOIN D'UN SERVICE ?
            </h2>
            <p style={{ color: 'rgba(255,255,255,0.6)', marginBottom: '2rem' }}>
              Trouvez le bon prestataire près de chez vous en quelques secondes.
            </p>
            <div style={{ display: 'flex', gap: '1rem', justifyContent: 'center', flexWrap: 'wrap' }}>
              <Link to="/prestataires" className="btn btn-primary btn-lg">Voir les prestataires</Link>
              <Link to="/inscription" className="btn btn-outline-white btn-lg">Devenir prestataire</Link>
            </div>
          </motion.div>
        </div>
      </div>
    </div>
  );
}
